import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useOwnerDetail, useTeamDashboard, useUpdateTeamOwner } from "../hooks/useTeam";
import type { OwnerEinsatz } from "../lib/team";
import { monthLabel, timeLabel } from "../lib/team";
import { Field } from "../components/team/TeamDialogs";
import { Avatar } from "./TeamPage";
import "../styles/team.css";

/**
 * Inhaber-Detail — dasselbe Gerüst wie das Mitarbeiterkonto (TeamMemberPage).
 *
 * Der Inhalt unterscheidet sich zwangsläufig: Inhaber zahlen sich nicht aus dem
 * Projekt aus, es gibt hier also kein Lohnkonto, sondern die Frage „wo ist meine
 * Zeit hingegangen“. Der RAHMEN muss aber derselbe sein — bis 17.08.2026 war er
 * es nicht: Reiter steckten im Kartenkopf statt auf der Seitenebene, die
 * Stammdaten hingen an einem Umschalter statt an einem Reiter, und Jahr wie
 * Ansicht standen nur im Speicher, nicht in der Adresse. Wer zwischen einem
 * Helfer und einem Inhaber wechselte, fand jedes Mal eine andere Seite vor.
 */
const REITER = [
  { id: "projekte", label: "Projekte" },
  { id: "einsaetze", label: "Einsätze" },
  { id: "stammdaten", label: "Stammdaten" },
] as const;

type ReiterId = (typeof REITER)[number]["id"];

export default function OwnerDetailPage() {
  const { ownerId } = useParams();
  const navigate = useNavigate();
  // Reiter und Jahr stehen in der Adresse — gleiche Regel wie beim Helfer:
  // ein Reload landet dort, wo man war, und ein Link lässt sich weitergeben.
  const [suchParams, setSuchParams] = useSearchParams();
  const jahrParam = Number(suchParams.get("jahr"));
  const jahr = jahrParam >= 2000 && jahrParam <= 2100 ? jahrParam : new Date().getFullYear();
  const reiter = (REITER.find((r) => r.id === suchParams.get("reiter"))?.id ?? "projekte") as ReiterId;
  const setzeParam = (key: string, wert: string) => {
    const naechste = new URLSearchParams(suchParams);
    naechste.set(key, wert);
    setSuchParams(naechste, { replace: true });
  };

  const detail = useOwnerDetail(ownerId, jahr);
  const dashboard = useTeamDashboard();
  const update = useUpdateTeamOwner();

  if (detail.isLoading) {
    return <div className="team-page"><div className="team-state large"><p>Inhaberkonto wird geladen …</p></div></div>;
  }
  if (detail.isError || !detail.data) {
    return (
      <div className="team-page">
        <button type="button" className="team-back" onClick={() => navigate("/team")}>← Zurück zum Team</button>
        <div className="team-state large">
          <p>Das Inhaberkonto konnte nicht geladen werden.</p>
          <button type="button" className="team-button secondary" onClick={() => detail.refetch()}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  const o = detail.data;
  const monate = gruppiereMonate(o.einsaetze);
  const jetzt = new Date().getFullYear();

  return (
    <div className="team-page team-detail-page">
      <button type="button" className="team-back" onClick={() => navigate("/team")}>← Zurück zum Team</button>

      <header className="team-member-head compact">
        <div className="team-member-identity">
          <Avatar name={o.name} />
          <div>
            <div className="team-member-title">
              <h1>{o.name}</h1>
              <span className={`team-pill ${o.active ? "success" : "muted"}`}>{o.active ? "Aktiv" : "Inaktiv"}</span>
              <span className="team-pill info">Inhaber</span>
            </div>
            <p>{o.email || "keine E-Mail"}{o.phone ? ` · ${o.phone}` : ""}</p>
          </div>
        </div>
        <div className="team-head-tools">
          {/* Ein Jahreswähler für die ganze Seite — wie beim Mitarbeiterkonto. */}
          <div className="team-year-nav">
            <button type="button" aria-label="Vorjahr" onClick={() => setzeParam("jahr", String(jahr - 1))}>‹</button>
            <strong>{jahr}</strong>
            <button type="button" aria-label="Folgejahr" disabled={jahr >= jetzt} onClick={() => setzeParam("jahr", String(jahr + 1))}>›</button>
          </div>
        </div>
      </header>

      <div className="team-detail-kpis">
        <article><span>Stunden {jahr}</span><strong>{stundenText(o.stunden_jahr)}</strong></article>
        <article><span>Stunden gesamt</span><strong>{stundenText(o.stunden_gesamt)}</strong></article>
        <article><span>Einsätze {jahr}</span><strong>{o.einsaetze_jahr}</strong></article>
        <article><span>Projekte {jahr}</span><strong>{o.projekte_jahr}</strong></article>
      </div>

      <nav className="team-tabs" role="tablist">
        {REITER.map((r) => (
          <button
            key={r.id}
            type="button"
            role="tab"
            aria-selected={reiter === r.id}
            className={reiter === r.id ? "aktiv" : ""}
            onClick={() => setzeParam("reiter", r.id)}
          >
            {r.label}
            {r.id === "projekte" && o.projekte.length > 0 && <em>{o.projekte.length}</em>}
            {r.id === "einsaetze" && o.einsaetze.length > 0 && <em>{o.einsaetze.length}</em>}
          </button>
        ))}
      </nav>

      <div className="team-tab-inhalt">
        {reiter === "projekte" && (
          o.projekte.length === 0 ? (
            <div className="team-state"><p>Für {jahr} sind keine Einsätze erfasst.</p></div>
          ) : (
            <div className="team-months">
              {o.projekte.map((p) => (
                <article
                  className="team-work-row ohne-datum"
                  key={p.projekt_id || p.projekt_title}
                  role={p.projekt_id ? "button" : undefined}
                  tabIndex={p.projekt_id ? 0 : undefined}
                  onClick={() => p.projekt_id && navigate(`/projekte/${p.projekt_id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && p.projekt_id) navigate(`/projekte/${p.projekt_id}`);
                  }}
                  style={p.projekt_id ? { cursor: "pointer" } : undefined}
                >
                  <div className="team-work-main">
                    <strong>{p.projekt_title || "Ohne Projekt"}</strong>
                    <span>{p.einsaetze} {p.einsaetze === 1 ? "Einsatz" : "Einsätze"}</span>
                  </div>
                  <div className="team-work-status"><strong>{stundenText(p.stunden)}</strong></div>
                </article>
              ))}
            </div>
          )
        )}

        {reiter === "einsaetze" && (
          monate.length === 0 ? (
            <div className="team-state"><p>Für {jahr} sind keine Einsätze erfasst.</p></div>
          ) : (
            <div className="team-months">
              {monate.map((m) => (
                <section className="team-month" key={m.key}>
                  <header>
                    <div>
                      <h3>{monthLabel(m.key)}</h3>
                      <span>{m.eintraege.length} {m.eintraege.length === 1 ? "Einsatz" : "Einsätze"}</span>
                    </div>
                    <strong>{stundenText(m.stunden)}</strong>
                  </header>
                  {m.eintraege.map((e) => (
                    <article className="team-work-row" key={e.slot_id}>
                      <time dateTime={e.datum}>
                        <strong>{new Date(`${e.datum}T12:00:00`).toLocaleDateString("de-DE", { day: "2-digit" })}</strong>
                        <span>{new Date(`${e.datum}T12:00:00`).toLocaleDateString("de-DE", { weekday: "short" })}</span>
                      </time>
                      <div className="team-work-main">
                        <strong>{e.projekt_title || "Ohne Projektbezeichnung"}</strong>
                        <span>
                          {timeLabel(e.start_at || "")}–{timeLabel(e.end_at || "")} · {stundenText(e.stunden)}
                          {e.pause_stunden ? ` · ${stundenText(e.pause_stunden)} Pause` : ""}
                        </span>
                        {e.note && <small>{e.note}</small>}
                      </div>
                      <div className="team-work-status">
                        <strong>{stundenText(e.stunden)}</strong>
                        {e.confirm_status && e.confirm_status !== "bestaetigt" && (
                          <span className="team-pill muted">{e.confirm_status}</span>
                        )}
                      </div>
                    </article>
                  ))}
                </section>
              ))}
            </div>
          )
        )}

        {reiter === "stammdaten" && (
          <OwnerStammdaten
            owner={o}
            busy={update.isPending}
            fehler={update.isError}
            gespeichert={update.isSuccess}
            onSpeichern={(patch) => update.mutate({ id: o.id, patch })}
          />
        )}
      </div>

      {dashboard.data && dashboard.data.owners.length > 1 && (
        <p className="team-hint">
          Weitere Inhaber:{" "}
          {dashboard.data.owners
            .filter((x) => x.id !== o.id)
            .map((x) => (
              <button key={x.id} type="button" className="team-linklike" onClick={() => navigate(`/team/owner/${x.id}`)}>
                {x.name}
              </button>
            ))}
        </p>
      )}
    </div>
  );
}

function OwnerStammdaten({
  owner,
  busy,
  fehler,
  gespeichert,
  onSpeichern,
}: {
  owner: { name: string; phone: string; email: string; active: boolean };
  busy: boolean;
  fehler: boolean;
  gespeichert: boolean;
  onSpeichern: (patch: { name: string; phone: string; email: string; active: boolean }) => void;
}) {
  const [f, setF] = useState({
    name: owner.name,
    phone: owner.phone,
    email: owner.email,
    active: owner.active,
  });
  return (
    <section className="team-card">
      <div className="team-form-grid">
        <Field label="Name"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Telefon"><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
        <Field label="E-Mail"><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Status">
          <select value={f.active ? "1" : "0"} onChange={(e) => setF({ ...f, active: e.target.value === "1" })}>
            <option value="1">Aktiv</option>
            <option value="0">Inaktiv</option>
          </select>
        </Field>
      </div>
      {fehler && <p className="team-error">Die Stammdaten konnten nicht gespeichert werden.</p>}
      <div className="team-inline-actions">
        <button type="button" className="team-button primary" disabled={!f.name.trim() || busy} onClick={() => onSpeichern(f)}>
          {busy ? "Speichert …" : "Änderungen speichern"}
        </button>
        {gespeichert && !busy && <span className="team-saved-hint">Gespeichert.</span>}
      </div>
    </section>
  );
}

function stundenText(n: number): string {
  return `${(n || 0).toLocaleString("de-DE", { maximumFractionDigits: 2 })} Std.`;
}

function gruppiereMonate(eintraege: OwnerEinsatz[]) {
  const keys = [...new Set(eintraege.map((e) => e.datum.slice(0, 7)))].sort().reverse();
  return keys.map((key) => {
    const liste = eintraege.filter((e) => e.datum.startsWith(key));
    return { key, eintraege: liste, stunden: liste.reduce((s, e) => s + e.stunden, 0) };
  });
}
